import {
  AcquisitionAnalyticsResponseDto,
  AnalyticsControllerGetOverviewAnalyticsV1RequestParams,
  AnalyticsSegmentDto,
  CampaignDetailAnalyticsResponseDto,
  CampaignsAnalyticsResponseDto,
  CohortsAnalyticsResponseDto,
  InsightsAnalyticsResponseDto,
  MembersAnalyticsResponseDto,
  OverviewAnalyticsResponseDto,
  PassesAnalyticsResponseDto,
  PointsAnalyticsResponseDto,
  RevenueAnalyticsResponseDto,
  RewardsAnalyticsResponseDto,
  TiersAnalyticsResponseDto,
} from "../../services/api";
import { AnalyticsRangePreset } from "../../enums";

export const ANALYTICS_FEATURE_KEY = "analytics";

export type AnalyticsCompare = NonNullable<AnalyticsControllerGetOverviewAnalyticsV1RequestParams["compare"]>;
export type AnalyticsGranularity = NonNullable<AnalyticsControllerGetOverviewAnalyticsV1RequestParams["granularity"]>;

export interface AnalyticsFilters {
  preset: AnalyticsRangePreset;
  compare: AnalyticsCompare;
  /** null lets the API pick it from the range length. */
  granularity: AnalyticsGranularity | null;
}

export interface AnalyticsSectionState<T> {
  data: T | null;
  /** Preset of the stored section data; campaign detail windows are chosen by the API. */
  loadedPreset?: AnalyticsRangePreset;
  /** Preset of the last request; a late answer for another one (Home and Analytics share sections) is dropped. */
  requestedPreset?: AnalyticsRangePreset;
  loading: boolean;
  error: string | null;
}

/** One entry per endpoint; each tab loads only the sections it shows. */
export interface AnalyticsSections {
  overview: AnalyticsSectionState<OverviewAnalyticsResponseDto>;
  insights: AnalyticsSectionState<InsightsAnalyticsResponseDto>;
  segments: AnalyticsSectionState<AnalyticsSegmentDto[]>;
  members: AnalyticsSectionState<MembersAnalyticsResponseDto>;
  revenue: AnalyticsSectionState<RevenueAnalyticsResponseDto>;
  cohorts: AnalyticsSectionState<CohortsAnalyticsResponseDto>;
  points: AnalyticsSectionState<PointsAnalyticsResponseDto>;
  tiers: AnalyticsSectionState<TiersAnalyticsResponseDto>;
  rewards: AnalyticsSectionState<RewardsAnalyticsResponseDto>;
  passes: AnalyticsSectionState<PassesAnalyticsResponseDto>;
  acquisition: AnalyticsSectionState<AcquisitionAnalyticsResponseDto>;
  campaigns: AnalyticsSectionState<CampaignsAnalyticsResponseDto>;
}

export type AnalyticsSection = keyof AnalyticsSections;

export interface AnalyticsState extends AnalyticsSections {
  filters: AnalyticsFilters;
  /** Per campaign id: the performance block of each campaign page. */
  campaignDetails: Record<string, AnalyticsSectionState<CampaignDetailAnalyticsResponseDto>>;
}

const emptySection = { data: null, loading: false, error: null };

export const initialState: AnalyticsState = {
  filters: { preset: AnalyticsRangePreset.last12Months, compare: "previous_period", granularity: null },
  overview: emptySection,
  insights: emptySection,
  segments: emptySection,
  members: emptySection,
  revenue: emptySection,
  cohorts: emptySection,
  points: emptySection,
  tiers: emptySection,
  rewards: emptySection,
  passes: emptySection,
  acquisition: emptySection,
  campaigns: emptySection,
  campaignDetails: {},
};
